import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ButtonItemComponentObject } from '@trendbuild/trend-cloud-api';
import { waTemplateButtonAction, waTemplateButtonLabel, WhatsAppButton } from 'lib-trend-core';
import { TemplateFormEngine } from './template-form-engine';

/**
 * The message as the end customer receives it (RF-26, RF-27, RF-47).
 *
 * This component only MAPS the form onto `<chat-wa-preview>`, the shared WhatsApp preview in
 * `lib-trend-core` — phone shell, chat header, bubble, buttons and timestamp all come from there.
 * An earlier version drew its own bubble because `wa-template-message` turned out to be a dialog
 * and could not be reused; that was the wrong conclusion from looking at only one candidate.
 *
 * It reads the same FormGroup the operator is editing, so it needs no refresh mechanism: change
 * detection recomputes it as the form changes.
 *
 * SPEC-2026-08-whatsapp-cloud-api-template-builder.
 */
@Component({
    selector: 'template-preview',
    templateUrl: './template-preview.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TemplatePreviewComponent {

  @Input() formGroup: FormGroup;
  @Input() engine: TemplateFormEngine;

  /** Header text with the samples substituted where they were typed (RF-27). */
  get title(): string {
    if (this.engine?.isMediaHeader() || this.formGroup?.get('headerFormat')?.value === 'LOCATION') {
      return '';
    }
    return this.engine?.applyExamples(
      this.formGroup?.get('headerText')?.value,
      this.engine.headerParameters,
      this.engine.headerExamples.value,
    );
  }

  get message(): string {
    return this.engine?.applyExamples(
      this.formGroup?.get('body')?.value,
      this.engine.bodyParameters,
      this.engine.bodyExamples.value,
    );
  }

  get footer(): string {
    return this.formGroup?.get('footer')?.value ?? '';
  }

  get buttons(): Array<WhatsAppButton> {
    return (this.engine?.buttons?.getRawValue() ?? [])
      .map((button: { [key: string]: unknown }) => ({
        label: waTemplateButtonLabel(button as unknown as ButtonItemComponentObject),
        type: waTemplateButtonAction(button['type'] as string),
      }));
  }

  /** The shared preview draws buttons only in its `button` shape. */
  get templateType(): 'text' | 'button' {
    return this.buttons.length > 0 ? 'button' : 'text';
  }

  /**
   * Only an already-uploaded media has something to show: a new attachment gives back an opaque
   * handle, not a URL, so there is nothing to render until the template is saved and read again.
   */
  get mediaSrc(): string {
    return this.engine?.currentMediaUrl ?? '';
  }
}
